import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardMedia,
  Divider,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// Icons
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import LaptopIcon from '@mui/icons-material/Laptop';
import TvIcon from '@mui/icons-material/Tv';
import ComputerIcon from '@mui/icons-material/Computer';
import TabletIcon from '@mui/icons-material/Tablet';

// Import logo
import logo0 from '../assets/logo0.png';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
  }
}));

const GreenButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#006938',
  color: 'white',
  '&:hover': {
    backgroundColor: '#004d2b',
  },
  padding: '10px 20px',
  borderRadius: '4px',
  fontWeight: 600,
  textTransform: 'none'
}));

const OutlinedGreenButton = styled(Button)(({ theme }) => ({
  borderColor: '#006938',
  color: '#006938',
  '&:hover': {
    borderColor: '#004d2b',
    backgroundColor: 'rgba(0, 105, 56, 0.04)'
  },
  padding: '10px 20px',
  borderRadius: '4px',
  fontWeight: 600,
  textTransform: 'none'
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #006938',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: 'rgba(0, 105, 56, 0.04)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 105, 56, 0.08)',
    borderColor: '#004d2b'
  }
}));

const AIdiagnostics = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [deviceType, setDeviceType] = useState('');
  const [deviceBrand, setDeviceBrand] = useState('');
  const [issue, setIssue] = useState('');
  const [file, setFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  
  const steps = ['Select Device', 'Describe Issue', 'Upload Images', 'AI Analysis'];
  
  const deviceTypes = [
    { value: 'smartphone', label: 'Smartphone', icon: <SmartphoneIcon /> },
    { value: 'laptop', label: 'Laptop', icon: <LaptopIcon /> },
    { value: 'desktop', label: 'Desktop', icon: <ComputerIcon /> },
    { value: 'tablet', label: 'Tablet', icon: <TabletIcon /> },
    { value: 'tv', label: 'TV', icon: <TvIcon /> }
  ];
  
  const deviceBrands = {
    smartphone: ['Apple', 'Samsung', 'Xiaomi', 'Huawei', 'Google', 'OnePlus', 'Other'],
    laptop: ['Apple', 'Dell', 'HP', 'Lenovo', 'Asus', 'Acer', 'Other'],
    desktop: ['Dell', 'HP', 'Lenovo', 'Asus', 'Custom Build', 'Other'],
    tablet: ['Apple', 'Samsung', 'Microsoft', 'Lenovo', 'Amazon', 'Other'],
    tv: ['Samsung', 'LG', 'Sony', 'TCL', 'Hisense', 'Philips', 'Other']
  };
  
  const commonIssues = {
    smartphone: ['Won\'t turn on', 'Battery drains quickly', 'Screen is cracked', 'Overheating', 'Slow performance', 'Other'],
    laptop: ['Won\'t boot', 'Battery issues', 'Screen problems', 'Overheating', 'Keyboard not working', 'Other'],
    desktop: ['No power', 'Blue screen', 'Slow performance', 'Strange noises', 'No display', 'Other'],
    tablet: ['Won\'t charge', 'Screen issues', 'Not responding', 'Battery drains quickly', 'Wi-Fi problems', 'Other'],
    tv: ['No picture', 'No sound', 'Lines on screen', 'Won\'t turn on', 'Smart features not working', 'Other']
  };

  const handleNext = () => {
    if (activeStep === 3) {
      // Start analysis
      setIsAnalyzing(true);
      
      // Simulate AI analysis
      setTimeout(() => {
        setIsAnalyzing(false);
        setAnalysisComplete(true);
        setDiagnosisResult({
          problem: 'Battery Connection Issue',
          confidence: 92,
          description: 'The AI has detected a potential issue with the battery connection. This is commonly caused by loose connections or corrosion on the battery terminals.',
          suggestedFixes: [
            'Clean the battery terminals with a soft cloth',
            'Check for loose connections and secure them',
            'Test the battery voltage with a multimeter',
            'If problems persist, consider battery replacement'
          ],
          severity: 'Medium',
          estimatedRepairTime: '15-30 minutes',
          partsNeeded: ['Battery cleaning kit', 'Possibly new battery']
        });
      }, 3000);
      
      return;
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setDeviceType('');
    setDeviceBrand('');
    setIssue('');
    setFile(null);
    setAnalysisComplete(false);
    setDiagnosisResult(null);
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      setFile(event.dataTransfer.files[0]);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select your device type
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {deviceTypes.map((type) => (
                <Grid item xs={6} sm={4} md={2.4} key={type.value}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: deviceType === type.value ? '2px solid #006938' : '1px solid #e0e0e0',
                      bgcolor: deviceType === type.value ? 'rgba(0, 105, 56, 0.04)' : 'white',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        borderColor: '#006938'
                      }
                    }}
                    onClick={() => setDeviceType(type.value)}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Box sx={{ fontSize: 40, color: '#006938', mb: 1 }}>
                        {type.icon}
                      </Box>
                      <Typography variant="body1" fontWeight={500}>
                        {type.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {deviceType && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Brand</InputLabel>
                <Select
                  value={deviceBrand}
                  onChange={(e) => setDeviceBrand(e.target.value)}
                  label="Select Brand"
                >
                  {deviceBrands[deviceType].map((brand) => (
                    <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Describe the issue you're experiencing
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Common Issues</InputLabel>
              <Select
                value={issue}
                onChange={(e) => setIssue(e.target.value)}
                label="Common Issues"
              >
                {deviceType && commonIssues[deviceType].map((issue) => (
                  <MenuItem key={issue} value={issue}>{issue}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Provide additional details about your issue"
              placeholder="Describe when the issue started, what you've already tried, and any error messages you've seen..."
              variant="outlined"
            />
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload images of your device (optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Clear images help our AI provide more accurate diagnostics. Upload photos showing the issue or error messages.
            </Typography>
            
            <input
              type="file"
              accept="image/*"
              id="file-upload"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              multiple
            />
            
            <UploadBox
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-upload').click()}
            >
              <UploadFileIcon sx={{ fontSize: 48, color: '#006938', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Drag & Drop or Click to Upload
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports JPG, PNG, HEIC (max 10MB)
              </Typography>
              
              {file && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(0, 105, 56, 0.08)', borderRadius: 1 }}>
                  <Typography variant="body2">
                    {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                  </Typography>
                </Box>
              )}
            </UploadBox>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ textAlign: 'center' }}>
            {isAnalyzing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <CircularProgress size={60} sx={{ color: '#006938' }} />
                <Typography variant="h6">
                  AI is analyzing your device issue...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This may take a few moments. Our AI is processing your information to provide an accurate diagnosis.
                </Typography>
              </Box>
            ) : analysisComplete ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <CheckCircleIcon sx={{ fontSize: 60, color: '#006938' }} />
                </Box>
                <Typography variant="h5" gutterBottom>
                  Diagnosis Complete
                </Typography>
                
                <StyledPaper sx={{ mt: 3, textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {diagnosisResult.problem}
                    </Typography>
                    <Chip 
                      label={`${diagnosisResult.confidence}% Confidence`} 
                      color="success" 
                      sx={{ bgcolor: '#006938' }}
                    />
                  </Box>
                  
                  <Typography paragraph>
                    {diagnosisResult.description}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Suggested Fixes:
                  </Typography>
                  
                  <Box component="ul" sx={{ pl: 2 }}>
                    {diagnosisResult.suggestedFixes.map((fix, index) => (
                      <Box component="li" key={index} sx={{ mb: 1 }}>
                        <Typography>{fix}</Typography>
                      </Box>
                    ))}
                  </Box>
                  
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Severity:
                      </Typography>
                      <Typography>{diagnosisResult.severity}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Estimated Repair Time:
                      </Typography>
                      <Typography>{diagnosisResult.estimatedRepairTime}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Parts Needed:
                      </Typography>
                      <Typography>{diagnosisResult.partsNeeded.join(', ')}</Typography>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <GreenButton 
                      startIcon={<BuildIcon />}
                      onClick={() => navigate('/repair-guide')}
                    >
                      View Detailed Repair Guide
                    </GreenButton>
                    <OutlinedGreenButton
                      onClick={() => navigate('/marketplace')}
                    >
                      Find Parts
                    </OutlinedGreenButton>
                  </Box>
                </StyledPaper>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Ready to analyze your device issue
                </Typography>
                <Typography variant="body1" paragraph>
                  Our AI will analyze the information you've provided to diagnose the issue and suggest solutions.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  sx={{ 
                    bgcolor: '#006938',
                    '&:hover': {
                      bgcolor: '#004d2b'
                    }
                  }}
                >
                  Start Analysis
                </Button>
              </Box>
            )}
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img 
            src={logo0} 
            alt="Logo" 
            style={{ 
              height: '40px',
              marginBottom: '20px'
            }} 
          />
          <Typography 
            variant="h3" 
            component="h1" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              color: '#006938',
              mb: 2
            }}
          >
            AI Device Diagnostics
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            color="text.secondary"
            sx={{ 
              maxWidth: '800px',
              mb: 4
            }}
          >
            Our advanced AI will analyze your device issues and provide accurate diagnostics and repair recommendations
          </Typography>
        </Box>

        {/* Main Content */}
        <StyledPaper>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <Box sx={{ mt: 4, mb: 4 }}>
            {getStepContent(activeStep)}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0 || isAnalyzing}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            <Box>
              {analysisComplete && (
                <Button 
                  onClick={handleReset} 
                  sx={{ mr: 1 }}
                >
                  Start New Diagnosis
                </Button>
              )}
              {!analysisComplete && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={
                    (activeStep === 0 && (!deviceType || !deviceBrand)) ||
                    (activeStep === 1 && !issue) ||
                    isAnalyzing
                  }
                  sx={{ 
                    bgcolor: '#006938',
                    '&:hover': {
                      bgcolor: '#004d2b'
                    }
                  }}
                >
                  {activeStep === steps.length - 1 ? 'Analyze' : 'Next'}
                </Button>
              )}
            </Box>
          </Box>
        </StyledPaper>
        
        {/* Additional Info */}
        <Grid container spacing={3} sx={{ mt: 4 }}>
          <Grid item xs={12} md={4}>
            <StyledPaper>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SearchIcon sx={{ fontSize: 40, color: '#006938', mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  Accurate Diagnostics
                </Typography>
              </Box>
              <Typography>
                Our AI has been trained on millions of device issues to provide precise and reliable diagnostics for your specific problem.
              </Typography>
            </StyledPaper>
          </Grid>
          <Grid item xs={12} md={4}>
            <StyledPaper>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SettingsIcon sx={{ fontSize: 40, color: '#006938', mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  Detailed Solutions
                </Typography>
              </Box>
              <Typography>
                Get step-by-step repair guides, parts recommendations, and expert advice tailored to your specific device issue.
              </Typography>
            </StyledPaper>
          </Grid>
          <Grid item xs={12} md={4}>
            <StyledPaper>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ fontSize: 40, color: '#006938', mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  DIY or Professional Help
                </Typography>
              </Box>
              <Typography>
                Choose to follow our DIY repair guides or connect with certified technicians in our network for professional assistance.
              </Typography>
            </StyledPaper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AIdiagnostics; 